/* global google */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React from 'react';
import PropTypes from 'prop-types';
import { withSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import axios from 'axios';
import compose from 'lodash/fp/compose';
import debounce from 'lodash/debounce';
import get from 'lodash/get';
import Lottie from 'react-lottie';
import Downshift from 'downshift';
import { withNamespaces } from 'react-i18next';
import { showElementCss, hideElementCss } from '../../../utils/responsive';
import { getCoordinates } from '../../../utils/googleMaps';
import createButtonCss from '../Button/Button';
import Select from '../Form/Dropdown';
import { Column, Row, MediaQueries } from '../../../styles/Grid';
import { Icon } from '../Icon';
import { LIGHT_THEME, DARK_THEME } from './constants';
import {
  SOLUTIONS,
  SOLUTION_IDS,
  GLOBAL_SEARCH_PARAM,
} from '../../../utils/constants';
import { RICH_FILTERING, MapAndContact } from '../../../utils/dictionarykeys';
import HighlightedText from '../../ui/HighlightedText';
import { processButtonData } from '../../../utils/analytics';
import {
  DYNAMIC_PAGE,
  MINIMUM_CHARACTERS_TO_SEARCH,
  NAV,
  SEARCH_PARAMETER,
  SOLUTIONS_TEMPLATE_IDS,
  WORSKSPACE_PARAMETER,
  STATUS_OK,
  PLACES_READY_EVENT,
} from '../../../utils/constants';
import { SafePureComponent } from '../../../utils/SafePureComponent';
import searchAnimation from '../../../assets/animations/search-load.json';
import {
  searchFieldCss,
  customContainerInputCss,
  customContainerInputWithResultsCss,
  customDropdownLabelCss,
  formCss,
  iconContainerCss,
  inputCss,
  searchIconCss,
  searchResultItemCss,
  highlightedSearchResultItemCss,
  searchResultsCss,
  searchResultTextCss,
  submitCss,
  topOffsetSearchResultsCss,
  searchButtonLabelCss,
  searchButtonLabelLoadingCss,
  searchAnimationCss,

  // Dark theme styles
  darkCustomInputContainerCss,
  darkCustomInputFieldCss,
  darkCustomDropdownLabelCss,
  darkDropdownPlaceholderCss,
  darkIconContainerCss,
  darkIconCss,
  darkSearchIconCss,
  darkSearchResultItemCss,
  darkSearchResultsCss,
  darkSubmitCss,
  darkSelectContainerCss,
  darkSearchInputColCss,
  darkSearchButtonColCss,
  darkSearchResultTextCss,
  darkSearchResultTextMatchCss,
  darkNoResultsCss,
  iconErrorCss,
  defaultNoResultsCss,
  noResultsCss,
} from './index.styles';

const animationOptions = {
  loop: true,
  autoplay: false,
  animationData: searchAnimation,
};

const dict = { ...MapAndContact, ...RICH_FILTERING };

const LOCATION_SEARCH_EVENT = 'locationSearch';
const LOCATION_SUGGESTION_EVENT = 'locationSuggestion';
const DEFAULT_SOLUTION = '/office-space';
const MAP_ID = 'map';

const getViewportPlaceService = (placesService, location) =>
  new Promise((resolve, reject) => {
    placesService.textSearch({ query: location }, (results, status) => {
      if (status === STATUS_OK) {
        const { viewport } = get(results, '[0].geometry');

        resolve(getCoordinates(viewport));
      } else {
        reject({});
      }
    });
  });

export class Search extends SafePureComponent {
  constructor(props) {
    super(props);
    this.t = props.t;
    this.state = {
      selectedWorkspace: null,
      location: '',
      searchResults: [],
      isLoading: false,
      selectedText: '',
      isTheSubmitDisabled: false,
      noResults: false,
    };
  }

  inputRef = React.createRef();

  placesService = null;

  callAutocompleteService = debounce(async () => {
    const { lng } = this.props;
    const { location } = this.state;

    try {
      const results = await axios(
        `/api/v1/autocomplete?term=${location}&lang=${lng}`,
      );

      this.setState({
        searchResults: get(results, 'data', []),
        isLoading: false,
      });
    } catch (e) {
      this.setState({
        searchResults: [],
        isLoading: false,
      });
    }
  }, 500);

  componentDidMount() {
    this.focusSearch();
    const { defaultOption } = this.getDefaultValueAndOptions();
    const { richFiltering } = this.props;
    defaultOption && this.setState({ selectedWorkspace: defaultOption.value });

    if (richFiltering) {
      if (window.placesAPIReady) {
        this.initGoogleMaps();
      } else {
        window.addEventListener(PLACES_READY_EVENT, this.initGoogleMaps);
      }
    }
  }

  initGoogleMaps = () => {
    const map = new google.maps.Map(document.querySelector(`#${MAP_ID}`));
    this.placesService = new google.maps.places.PlacesService(map);
  };

  componentDidUpdate = (_prevProps, prevState) => {
    const { location } = this.state;

    if (
      prevState.location !== location &&
      location.length >= MINIMUM_CHARACTERS_TO_SEARCH
    ) {
      this.setState({ isLoading: true });
      this.callAutocompleteService();
    } else if (
      prevState.location !== location &&
      location.length < MINIMUM_CHARACTERS_TO_SEARCH
    ) {
      this.setState({ searchResults: [] });
    }
  };

  setDataLayerObj = (
    solutionId,
    searchTerm,
    event,
    suggestionUrl,
    suggestion = '',
  ) => {
    // this value is undefined in case the event triggerer was LOCATION_SUGGESTION_EVENT.
    let solution = undefined;
    const data = {
      searchLocation: `${searchTerm}|${suggestion}|${suggestionUrl}`,
      suggestionUrl:
        event === LOCATION_SUGGESTION_EVENT ? suggestionUrl : undefined,
      event,
      eventCallback: function () {
        window.location.href = suggestionUrl;
      },
    };

    if (event === LOCATION_SEARCH_EVENT) {
      // TODO: send solution id instead of a name to the datalayer object.
      // This default solution value was selected here because office space does not has a path value.
      solution = solutionId || DEFAULT_SOLUTION;
    }

    data.solution = solution;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(data);
  };

  onDropdownChange = event => {
    this.setState({ selectedWorkspace: event.target.value });
  };

  onInputChange = event => {
    const { value } = event.target;
    this.setState({ location: value, selectedText: value });
  };

  onDropdownItemSelect = (item = {}) => {
    const url = this.getResultURL(item);
    const selectedText = get(item, 'Name', '');

    this.setState({ isLoading: true, selectedText, isTheSubmitDisabled: true });
    this.setDataLayerObj(
      this.state.selectedWorkspace,
      this.state.location,
      LOCATION_SUGGESTION_EVENT,
      url,
      selectedText,
    );
  };

  getViewportPlace = async (lang, selectedWorkspace) => {
    const { location, searchResults } = this.state;
    const { placesService } = this;
    const locationSuggestion = get(searchResults[0], 'Name', null);
    const replacedSelectedWorkspace = selectedWorkspace.replace('/', '');
    const parsedSelectedWorkspace = replacedSelectedWorkspace
      ? replacedSelectedWorkspace
      : DEFAULT_SOLUTION.replace('/', '');
    const searchStr = locationSuggestion ? locationSuggestion : location;

    const viewportPlace = await getViewportPlaceService(
      placesService,
      searchStr,
    ).catch(() => ({}));

    const { northeast, southwest } = viewportPlace;
    if (northeast && southwest) {
      return `/${lang}/results?search=${location}&ws=${parsedSelectedWorkspace}&northeast=${northeast}&southwest=${southwest}&${GLOBAL_SEARCH_PARAM}=true`;
    } else return null;
  };

  getNewUrl = async () => {
    const { selectedWorkspace } = this.state;
    const { lng } = this.props;

    const urlSearch = await this.getViewportPlace(lng, selectedWorkspace);

    if (!urlSearch) {
      this.setState({ isLoading: false, noResults: true });
      this.blurSearch();
      return;
    }

    return urlSearch;
  };

  getUrl = () => {
    const { location, selectedWorkspace } = this.state;
    const { lng } = this.props;
    let redirectUrl = null;

    const baseUrl = `/${lng}/${DYNAMIC_PAGE}?${SEARCH_PARAMETER}=${location}`;
    redirectUrl = baseUrl;

    this.setState({ isLoading: true });

    if (!selectedWorkspace) return baseUrl;

    const parsedSelectedWorkspace = selectedWorkspace.replace('/', '');
    redirectUrl = `${baseUrl}&${WORSKSPACE_PARAMETER}=${parsedSelectedWorkspace}`;

    return redirectUrl;
  };

  onSubmitForm = async event => {
    event.preventDefault();

    const { location, selectedWorkspace } = this.state;
    const { parent, richFiltering } = this.props;
    let redirectUrl;

    if (location.length < MINIMUM_CHARACTERS_TO_SEARCH) return;

    if (richFiltering) {
      redirectUrl = await this.getNewUrl();
      if (!redirectUrl) return;
    } else {
      redirectUrl = this.getUrl();
    }

    processButtonData(parent, this.t('search'));

    this.setDataLayerObj(
      selectedWorkspace,
      location,
      LOCATION_SEARCH_EVENT,
      redirectUrl,
    );
  };

  buildWorkspaceOptionsDropdown = workspaceOptions =>
    workspaceOptions
      .filter(workspaceOptions =>
        get(workspaceOptions, 'fields.IsSearchable.value', true),
      )
      .map(workspaceOptions => ({
        label: get(workspaceOptions, 'fields.Label.value', ''),
        value: get(workspaceOptions, 'fields.QueryString.value', ''),
      }));

  getSolutionsDefaultLabel(workspaceOptions) {
    const solutionProtonId = get(
      this.props.sitecoreContext,
      'route.fields.SolutionProtonId.value',
      undefined,
    );

    const protonId = get(
      this.props.sitecoreContext,
      'route.fields.ProtonId.value',
      undefined,
    );

    // Solutions identifier (Proton Id) varies according to page.
    // - solutionProtonId is the solution's id on geo solution pages
    // - protonId is the solution's id on solution pages
    const solution = SOLUTION_IDS[solutionProtonId || protonId];

    // Return solution if valid else return a default option (office space)
    return workspaceOptions.find(element => {
      return get(element, 'fields.QueryString.value') === solution;
    })
      ? solution
      : SOLUTIONS.OFFICE_SPACE;
  }

  isLocationSolution = id =>
    id.split('-')[0] === SOLUTIONS_TEMPLATE_IDS.LOCATION;

  isGeoLocationSolution = id =>
    SOLUTIONS_TEMPLATE_IDS.GEO_LOCATION.includes(id.split('-')[0]);

  isDarkTheme = theme => theme === DARK_THEME;

  getIconName = templateId => {
    let iconName = 'pin-lines';

    if (this.isLocationSolution(templateId)) {
      iconName = 'office-space';
    }

    return iconName;
  };

  getResultURL = result => {
    const { selectedWorkspace } = this.state;
    const templateId = get(result, 'TemplateId', '');
    let resultURL = get(result, 'Url', '');

    if (this.isGeoLocationSolution(templateId) && selectedWorkspace) {
      resultURL = `${resultURL}${selectedWorkspace}`;
    }

    return resultURL;
  };

  getLayoutDistribution = () => {
    const { replaceSearchButtonWithIcon, theme } = this.props;
    const isDarkTheme = this.isDarkTheme(theme);
    let dropDownColumnDistribution = { sm: [6, 6], md: [3, 8], lg: 3, xl: 3 };
    let inputColumnDistribution = { sm: [6, 6], md: [3, 8], lg: 7, xl: 7 };
    let submitColumnDistribution = { sm: [6, 6], md: [2, 8], lg: 2, xl: 2 };
    let resultsColumnDistribution = { sm: [6, 6], md: [3, 8], lg: 7 };
    let resultsColumnOffsets = { md: 3, lg: 3 };

    if (replaceSearchButtonWithIcon) {
      dropDownColumnDistribution = {
        ...dropDownColumnDistribution,
        lg: 4,
      };
      inputColumnDistribution = { sm: [5, 6], md: 7, lg: 5, xl: 7 };
      submitColumnDistribution = { sm: [1, 6], md: [1, 8], lg: 3, xl: 2 };
    }

    if (isDarkTheme) {
      resultsColumnDistribution = { sm: [6, 6], md: [8, 8], lg: 12 };
      resultsColumnOffsets = { md: 0, lg: 0, xl: 3 };
    }

    return {
      dropDownColumnDistribution,
      inputColumnDistribution,
      submitColumnDistribution,
      resultsColumnDistribution,
      resultsColumnOffsets,
    };
  };

  renderSuggestions = ({ getMenuProps, getItemProps, highlightedIndex }) => {
    const { theme, replaceSearchButtonWithIcon } = this.props;
    const { searchResults, location } = this.state;
    const isDarkTheme = this.isDarkTheme(theme);

    return (
      <ul
        css={[
          searchResultsCss,
          isDarkTheme && darkSearchResultsCss,
          !replaceSearchButtonWithIcon && topOffsetSearchResultsCss,
        ]}
        {...getMenuProps()}
      >
        {searchResults.map((result, index) => (
          <li
            key={index}
            css={[
              searchResultItemCss,
              isDarkTheme && darkSearchResultItemCss,
              highlightedIndex === index && highlightedSearchResultItemCss,
            ]}
            {...getItemProps({ item: result, index })}
          >
            <Icon name={this.getIconName(get(result, 'TemplateId', ''))} />
            <HighlightedText
              textCss={[
                searchResultTextCss,
                isDarkTheme && darkSearchResultTextCss,
              ]}
              matchCss={isDarkTheme && darkSearchResultTextMatchCss}
              text={get(result, 'Name', '')}
              highlight={location}
            />
          </li>
        ))}
      </ul>
    );
  };

  focusSearch = () => {
    const { parent } = this.props;

    if (parent === NAV) {
      this.inputRef.current && this.inputRef.current.focus();
    }
  };

  blurSearch = () => this.inputRef.current.blur();

  handleFocus = () => {
    this.setState({ noResults: false });
  };

  /**
   * Maps the props workspace options into an Array representing the default
   * option and the Array of options in the {value: '', label: ''} format.
   * @return {Array}
   */
  getDefaultValueAndOptions = () => {
    const { workspaceOptions } = this.props;
    const workspaceOptionsDropdown = this.buildWorkspaceOptionsDropdown(
      workspaceOptions,
    );
    let firstOption = this.getSolutionsDefaultLabel(workspaceOptions);
    firstOption = workspaceOptionsDropdown.find(
      option => option.value === firstOption,
    );
    const solutionsDefaultOption = firstOption
      ? firstOption
      : { label: '', value: '' };
    return {
      defaultOption: solutionsDefaultOption,
      workspaceOptions: workspaceOptionsDropdown,
    };
  };

  safeRender() {
    const {
      searchResults,
      isLoading,
      selectedText,
      isTheSubmitDisabled,
      noResults,
    } = this.state;
    const {
      hasRow,
      replaceSearchButtonWithIcon,
      richFiltering,
      parent,
      theme,
    } = this.props;
    const isDarkTheme = this.isDarkTheme(theme);
    const {
      defaultOption,
      workspaceOptions,
    } = this.getDefaultValueAndOptions();

    let ContainerComponent = React.Fragment;

    if (hasRow) {
      ContainerComponent = Row;
    }

    const {
      dropDownColumnDistribution,
      inputColumnDistribution,
      submitColumnDistribution,
      resultsColumnDistribution,
      resultsColumnOffsets,
    } = this.getLayoutDistribution();
    return (
      <Downshift
        onChange={this.onDropdownItemSelect}
        itemToString={item => get(item, 'Name', '')}
      >
        {({
          getRootProps,
          getMenuProps,
          getItemProps,
          highlightedIndex,
          getInputProps,
          isOpen,
        }) => (
          <form onSubmit={this.onSubmitForm} css={formCss}>
            <ContainerComponent>
              <Column cols={{ sm: 6, md: 8, lg: 12 }}>
                <Row>
                  <Column cols={dropDownColumnDistribution}>
                    <Select
                      css={[inputCss, isDarkTheme && darkSelectContainerCss]}
                      options={workspaceOptions}
                      onChangeProp={this.onDropdownChange}
                      customLabelCss={[
                        customDropdownLabelCss,
                        isDarkTheme && darkCustomDropdownLabelCss,
                      ]}
                      customPlaceholderCss={
                        isDarkTheme && darkDropdownPlaceholderCss
                      }
                      customIconCss={isDarkTheme && darkIconCss}
                      ref={this.dropdown}
                      disablePlaceholder
                      defaultLabel={defaultOption.label}
                    />
                  </Column>
                  <Column
                    cols={inputColumnDistribution}
                    css={isDarkTheme && darkSearchInputColCss}
                  >
                    <div
                      css={[
                        customContainerInputCss,
                        Boolean(searchResults.length) &&
                          customContainerInputWithResultsCss,
                        isDarkTheme && darkCustomInputContainerCss,
                      ]}
                      {...getRootProps({})}
                    >
                      <input
                        autoComplete="off"
                        css={[
                          inputCss,
                          searchFieldCss,
                          isDarkTheme && darkCustomInputFieldCss,
                        ]}
                        name={`locationSearch-${parent}`}
                        onFocus={this.handleFocus}
                        placeholder={this.t(dict.WhereTitle)}
                        ref={this.inputRef}
                        {...getInputProps({
                          onChange: this.onInputChange,
                          value: selectedText,
                        })}
                      />
                    </div>
                  </Column>
                  <Column
                    cols={submitColumnDistribution}
                    css={isDarkTheme && darkSearchButtonColCss}
                  >
                    <button
                      css={[
                        createButtonCss(),
                        submitCss,
                        isDarkTheme && darkSubmitCss,
                        replaceSearchButtonWithIcon &&
                          showElementCss(MediaQueries.lgAndUp),
                      ]}
                      disabled={isTheSubmitDisabled}
                      data-search-btn="search"
                    >
                      <span
                        css={[
                          searchButtonLabelCss,
                          isLoading && searchButtonLabelLoadingCss,
                        ]}
                      >
                        {this.t('search')}
                      </span>
                      <span css={searchAnimationCss}>
                        <Lottie
                          options={animationOptions}
                          isClickToPauseDisabled
                          isStopped={!isLoading}
                          height={40}
                        />
                      </span>
                    </button>
                    <div id={MAP_ID}></div>
                    {replaceSearchButtonWithIcon && (
                      <div
                        css={[
                          iconContainerCss,
                          isDarkTheme && darkIconContainerCss,
                          replaceSearchButtonWithIcon &&
                            hideElementCss(MediaQueries.lgAndUp),
                        ]}
                      >
                        <button
                          css={[
                            searchIconCss,
                            isDarkTheme && darkSearchIconCss,
                          ]}
                          name="Search"
                          type="submit"
                        >
                          <i className="icon-search" />
                        </button>
                      </div>
                    )}
                  </Column>
                </Row>
                <Row>
                  <Column
                    cols={resultsColumnDistribution}
                    offsets={resultsColumnOffsets}
                  >
                    {richFiltering && noResults && (
                      <div
                        css={[
                          defaultNoResultsCss,
                          isDarkTheme && darkNoResultsCss,
                          !isDarkTheme && noResultsCss,
                        ]}
                      >
                        <i className="icon-error" css={iconErrorCss} />
                        {this.t(dict.NO_RESULTS)}
                      </div>
                    )}
                  </Column>
                </Row>
                <Row>
                  <Column
                    cols={resultsColumnDistribution}
                    offsets={resultsColumnOffsets}
                  >
                    {isOpen &&
                      Boolean(searchResults.length) &&
                      this.renderSuggestions({
                        getMenuProps,
                        getItemProps,
                        highlightedIndex,
                      })}
                  </Column>
                </Row>
              </Column>
            </ContainerComponent>
          </form>
        )}
      </Downshift>
    );
  }
}

Search.displayName = 'Search';

Search.defaultProps = {
  hasRow: true,
  parent: '',
  replaceSearchButtonWithIcon: false,
  t: key => key,
  theme: LIGHT_THEME,
  workspaceOptions: [],
};

const workspaceOptionType = {
  fields: PropTypes.shape({
    Label: PropTypes.shape({
      value: PropTypes.string,
    }),
    IsSearchable: PropTypes.shape({
      value: PropTypes.bool,
    }),
    QueryString: PropTypes.shape({
      value: PropTypes.string,
    }),
  }),
};

Search.propTypes = {
  hasRow: PropTypes.bool,
  parent: PropTypes.string,
  replaceSearchButtonWithIcon: PropTypes.bool,
  theme: PropTypes.oneOf([LIGHT_THEME, DARK_THEME]),
  workspaceOptions: PropTypes.arrayOf(PropTypes.shape(workspaceOptionType)),
};

export default compose(withNamespaces(), withSitecoreContext())(Search);
